import { CertificatStat } from "./stat-connexion.model";

export interface ApiLearner {
  moodleUserId: number;
  firstname: string;
  lastname: string;
  email: string;
  sexe: 'masculin' | 'feminin' | null;
  firstAccess: number;
  lastAccess: number;
  lastCourseAccess: number;
  neverConnected: boolean;
}
export interface ApprenantJamaisConnecte {
  firstname: string;
  lastname: string;
  certificat: string;
  email: string;
}

export interface ApiCertificat {
  certificateId: string;
  certificateName: string;
  totalEnrolled: number;
  totalConnected: number;
  totalNotConnected: number;
  totalNeverConnected: number;
  totalMale: number;
  totalFemale: number;
  learners: ApiLearner[];
}

export interface ApiCertificatReponse {
  totalEnrolledFode: number;
  certificates: ApiCertificat[];
}

function estConnecte(learner: ApiLearner): boolean {
  return learner.firstAccess > 0;
}

export function calculerTotauxDedupliquesDepuisApi(
  reponse: ApiCertificatReponse | undefined
): CertificatStat {
  const vide = () => ({ hommes: 0, femmes: 0, nonRenseigne: 0, total: 0 });

  const learnersUniques = new Map<string, ApiLearner>();

  for (const certif of reponse?.certificates ?? []) {
    if (!Array.isArray(certif.learners)) continue;

    for (const learner of certif.learners) {
      if (!learner.email) continue;
      const emailNormalise = learner.email.trim().toLowerCase();
      if (!learnersUniques.has(emailNormalise)) {
        learnersUniques.set(emailNormalise, learner);
      }
    }
  }

  const learners = Array.from(learnersUniques.values());

  const hommes = learners.filter((l) => l.sexe === 'masculin');
  const femmes = learners.filter((l) => l.sexe === 'feminin');
  const nonRenseigne = learners.filter((l) => l.sexe !== 'masculin' && l.sexe !== 'feminin');

  const hommesConnectes = hommes.filter(estConnecte);
  const femmesConnectes = femmes.filter(estConnecte);
  const nonRenseigneConnectes = nonRenseigne.filter(estConnecte);

  const hommesJamaisConnectes = hommes.filter((l) => !estConnecte(l));
  const femmesJamaisConnectes = femmes.filter((l) => !estConnecte(l));
  const nonRenseigneJamaisConnectes = nonRenseigne.filter((l) => !estConnecte(l));

  return {
    id: '',
    nom: 'Total',
    effectif: {
      hommes: hommes.length,
      femmes: femmes.length,
      nonRenseigne: nonRenseigne.length,
      total: learners.length,
    },
    effectifFode: 0,
    connectes: {
      hommes: hommesConnectes.length,
      femmes: femmesConnectes.length,
      nonRenseigne: nonRenseigneConnectes.length,
      total: hommesConnectes.length + femmesConnectes.length + nonRenseigneConnectes.length,
    },
    jamaisConnectes: {
      hommes: hommesJamaisConnectes.length,
      femmes: femmesJamaisConnectes.length,
      nonRenseigne: nonRenseigneJamaisConnectes.length,
      total: hommesJamaisConnectes.length + femmesJamaisConnectes.length + nonRenseigneJamaisConnectes.length,
    },
  };
}

export function mapReponseApiVersCertificatStats(
  reponse: ApiCertificatReponse
): CertificatStat[] {

  return (reponse?.certificates ?? []).map((c) => {

    const totalEnrolled = c.totalEnrolled ?? 0;
    const totalMale = c.totalMale ?? 0;
    const totalFemale = c.totalFemale ?? 0;

    const nonRenseigne = Math.max(
      0,
      totalEnrolled - totalMale - totalFemale
    );

    const learners = c.learners ?? [];
    const hommes = learners.filter(
      (l) => l.sexe === 'masculin'
    );

    const femmes = learners.filter(
      (l) => l.sexe === 'feminin'
    );

    const learnersNonRenseigne = learners.filter(
      (l) => l.sexe !== 'masculin' && l.sexe !== 'feminin'
    );

    const hommesConnectes = hommes.filter(estConnecte);
    const femmesConnectes = femmes.filter(estConnecte);
    const nonRenseigneConnectes = learnersNonRenseigne.filter(estConnecte);

    const hommesJamaisConnectes = hommes.filter(
      (l) => !estConnecte(l)
    );

    const femmesJamaisConnectes = femmes.filter(
      (l) => !estConnecte(l)
    );

    const nonRenseigneJamaisConnectes = learnersNonRenseigne.filter(
      (l) => !estConnecte(l)
    );

    return {
      id: c.certificateId,
      nom: c.certificateName,

      effectif: {
        hommes: totalMale,
        femmes: totalFemale,
        nonRenseigne,
        total: totalEnrolled,
      },
      effectifFode: 0,

      connectes: {
        hommes: hommesConnectes.length,
        femmes: femmesConnectes.length,
        nonRenseigne: nonRenseigneConnectes.length,
        total: c.totalConnected ?? 0,
      },

      jamaisConnectes: {
        hommes: hommesJamaisConnectes.length,
        femmes: femmesJamaisConnectes.length,
        nonRenseigne: nonRenseigneJamaisConnectes.length,
        total: c.totalNeverConnected ?? 0,
      },
    };
  });
}